import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { NotificationController } from 'core-components/library/popup'
import { IWidget } from 'core-components/sitebuilder/models/IWidget'
import { setWidgets } from 'core-components/sitebuilder/store/siteBuilderSlice'
import { saveToCache } from 'src/core/classes/cache/Cache'
import StorageKeys from 'src/core/classes/cache/StorageKeys'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { proxySiteBuilder } from 'src/core/classes/helpers/UrlHelper'
import { mapWidgets, mapWidgetToDB } from 'src/core/classes/models/Widget'
import { RootState } from 'src/core/store'
import Logger from 'src/core/utils/Logger'

import { getCustomQuery } from '../helpers/getCustomQuery'

interface IWidgetFetchResult {
  items: IWidget[]
  total: number
}

export const widgetApi = createApi({
  reducerPath: '@app/api/widgetApi',
  baseQuery: getCustomQuery({
    storageKey: StorageKeys.widgets,
    baseUrl: proxySiteBuilder('/mbe/api'),
    cachedUrl: [{ url: '/v2/widgets/search', method: 'POST' }],
  }),
  endpoints: (builder) => ({
    fetchWidgets: builder.query<IWidgetFetchResult, void>({
      query: () => {
        return {
          url: '/v2/widgets/search',
          method: 'POST',
          body: {},
        }
      },
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled
          const widgets = data.items
          dispatch(setWidgets(widgets))
          saveToCache(StorageKeys.widgets, widgets, true)
        } catch (err) {
          Logger.error('Failed to fetch roles:', err)
        }
      },
      transformResponse: (response: any) => {
        const rows = response['Data'] || []
        return {
          items: response instanceof Array ? response : mapWidgets(rows),
          total: response['Total'] || 0,
        }
      },
    }),
    addWidgetLocal: builder.mutation<IWidget, IWidget>({
      queryFn: async (widget, { getState, dispatch }) => {
        const state = (getState() as RootState).siteBuilder
        const index = state.widgets.findIndex((w) => w.i === widget.i)

        let widgets: IWidget[]
        if (index !== -1) {
          widgets = [
            ...state.widgets.slice(0, index),
            { ...widget, isNew: false },
            ...state.widgets.slice(index + 1),
          ]
        } else {
          widgets = [...state.widgets, { ...widget, isNew: false }]
        }

        dispatch(setWidgets(widgets))
        await saveToCache(StorageKeys.widgets, widgets)
        return { data: widget }
      },
    }),
    deleteWidget: builder.mutation<boolean, string>({
      async queryFn(id, { getState, dispatch }, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `/v2/widgets/${id}`,
          method: 'DELETE',
          body: {},
        })

        if ('error' in response) {
          return { error: response.error as FetchBaseQueryError }
        }

        const state = (getState() as RootState).siteBuilder
        const widgets = state.widgets.filter((w) => w.i !== id)

        dispatch(setWidgets(widgets))
        await saveToCache(StorageKeys.widgets, widgets)

        return { data: true, error: undefined }
      },
    }),
    getWidget: builder.mutation<IWidget | null, string>({
      queryFn: (id, { getState }) => {
        const state = (getState() as RootState).siteBuilder
        const data = state.widgets.find((w) => w.i === id) || null
        return { data }
      },
    }),
    fetchWidgetGroups: builder.query<string[], void>({
      query: () => ({
        url: `/v2/widgets/groups`,
        method: 'GET',
      }),
    }),
    saveWidget: builder.mutation<any, IWidget>({
      async queryFn(widget, _, _extraOptions, baseQuery) {
        const { i, isNew } = widget
        const payload = mapWidgetToDB(widget, getAppId())

        if (!isNew) {
          delete payload.AppId
          delete payload.Id
        }

        const url = isNew ? `/v2/widgets` : `/v2/widgets/${i}`
        const method = isNew ? 'POST' : 'PUT'

        const response: any = await baseQuery({
          url,
          method,
          body: payload,
        })

        if (response.error) {
          NotificationController.showError({
            message: `Something went wrong while ${isNew ? 'creating' : 'updating'} widget`,
          })
          Logger.error(response.error)
          return { error: response.error }
        }

        const widgetId = response.data?.Id || i

        if (widgetId) {
          await baseQuery({
            url: `/v2/widgets/${widget.i}/roles`,
            method: 'PUT',
            body: widget.roleIds,
          })
        }

        if (response.data?.Errors) {
          return { error: new Error(response.data?.Errors) }
        }

        return { data: response.data }
      },
    }),
  }),
})

export const {
  useFetchWidgetsQuery,
  useAddWidgetLocalMutation,
  useGetWidgetMutation,
  useDeleteWidgetMutation,
  useFetchWidgetGroupsQuery,
  useSaveWidgetMutation,
} = widgetApi
